import React from 'react';
import { Box, Card, CardHeader, Grid, styled, useTheme } from '@mui/material';
import LoadingScreen from 'src/frontend-common/components/LoadingScreen';

import { CustomWidthTooltip } from 'src/style/common';
import RiskChartSkeleton from 'src/pages/FeatureAnalysis/skeleton';
import {
  okDonutTooltip,
  koDonutTooltip,
  getActiveProduct,
  getScatterCluster,
  replaceKeys,
  calculateMinMaxRelativePercentage,
  createWaffleChartDataSingle,
  scatterPlotTooltip,
} from 'src/pages/FeatureAnalysis/utils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ScoreClusterLabels, scoreClusterLabelsValues } from 'src/frontend-common/dto/Types/ScoreClusterLabelType';
import { RowOnDesktop } from 'src/pages/FeatureAnalysis/style';
import EmptyState from 'src/frontend-common/components/EmptyState';
import { FroudVsRejection } from 'src/frontend-common/dto/Interfaces/FrontendAnalytics/IFrontendAnalytics';
import ErrorBoundary from 'src/frontend-common/components/MicroServiceErrorBundary';
import CustomerMetricsTable from 'src/pages/FeatureAnalysis/CustomerMetricsTable';
import ChartWrapper from 'src/frontend-common/components/Charts/Wrapper';
import { IAlertAnalytic } from 'src/frontend-common/dto/Interfaces/Analytics';
import FailureScreen from 'src/frontend-common/components/FailureScreen';
import ConfusionMatrixTable, { ConfusionMatrix } from 'src/pages/FeatureAnalysis/ConfusionMatrixTable';
import jsonParse from 'src/frontend-common/typesafe/jsonParse';

interface Props {
  activeTab: number;
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
  a11yProps: (index: number) => {
    id: string;
    'aria-controls': string;
  };
  dataAnalysis: any;
  isErrorDataAnalysis: boolean;
  isLoadingDataAnalysis: boolean;
  setFileDetails: React.Dispatch<React.SetStateAction<any>>;
  setProductLabels: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  setVerticalBarChartData: React.Dispatch<React.SetStateAction<IAlertAnalytic[] | undefined>>;
  setKoDataArray: React.Dispatch<React.SetStateAction<undefined>>;
  setActiveProduct: React.Dispatch<React.SetStateAction<string | undefined>>;
  setFeatureName: React.Dispatch<React.SetStateAction<string | undefined>>;
}

interface LineChartDataType {
  x: number;
  y: number;
  label: string;
}

export interface CustomerResolutionMetrics {
  resolution_id: string;
  customer_id: string;
  score: number;
  risk_count: number;
  trust_count: number;
}

interface FalseNegativeOrPositiveMetrics {
  data: { items: CustomerResolutionMetrics[] };
}

const AccuracyAnalysis: React.FC<Props> = ({
  activeTab,
  dataAnalysis,
  isErrorDataAnalysis,
  isLoadingDataAnalysis,
  setFileDetails,
  setProductLabels,
  setVerticalBarChartData,
  setKoDataArray,
  setActiveProduct,
  setFeatureName,
}) => {
  const [confusionMatrixData, setConfusionMatrixData] = React.useState<ConfusionMatrix | undefined>();
  const [riskChartOk, setRiskChartOk] = React.useState();
  const [parsedScatterData, setParsedData] = React.useState<Record<string, any> | undefined>(undefined);
  const [riskChartKo, setRiskChartKo] = React.useState();
  const [waffleChartClusterData, setWaffleChartClusterData] = React.useState<Record<ScoreClusterLabels, { x: number; y: number }[]>>();
  const [decomposedDistribution, setDecomposedDistribution] = React.useState<
    Record<ScoreClusterLabels, { a: number; n: number; p: string; raw_p: number }> | undefined
  >(undefined);
  const [scatterData, setScatterData] = React.useState<Record<string, { x: number; y: number; z: string; resolution_id: string }>>();
  const [lineChartData, setLineChartData] = React.useState<LineChartDataType[] | undefined>(undefined);
  const [froudVsRejection, setFroudVsRejection] = React.useState<FroudVsRejection[] | undefined>(undefined);
  const [falseNegativeMetrics, setFalseNegativeMetrics] = React.useState<FalseNegativeOrPositiveMetrics | undefined>(undefined);
  const [falsePositiveMetrics, setFalsePositiveMetrics] = React.useState<FalseNegativeOrPositiveMetrics | undefined>(undefined);

  const RiskChart = React.lazy(() => import('FrontendAnalytics/RiskChart'));

  const theme = useTheme();

  React.useEffect(() => {
    if (dataAnalysis?.item?.file_details) {
      const parsedData: {
        original_file_name: string;
        file_upload_time: string;
        n_records: string;
        n_errors: string;
        n_matching_ids: string;
        n_missing_ids: string;
        created_at: string;
        ok_label_details: string;
        ko_label_details: string;
      } | null = jsonParse(dataAnalysis.item?.file_details);

      setFileDetails({ ...parsedData });
    }

    if (dataAnalysis?.item?.value) {
      const parsedData = jsonParse<Record<string, any>>(dataAnalysis.item?.value);
      const parsedFeatureProductMapping = jsonParse<Record<string, any>>(dataAnalysis.item?.feature_product_mapping);
      if (parsedData) {
        const featureImportanceData: { name: string; pv: number }[] = parsedData['feature_importance']?.data;
        const valuesArray = featureImportanceData.map((e) => e.pv);
        const scoreDistribution = parsedData['score_cluster_distribution'];

        if (scoreDistribution) {
          setWaffleChartClusterData({
            bad: createWaffleChartDataSingle(scoreDistribution['bad']),
            poor: createWaffleChartDataSingle(scoreDistribution['poor']),
            moderate: createWaffleChartDataSingle(scoreDistribution['moderate']),
            good: createWaffleChartDataSingle(scoreDistribution['good']),
            high: createWaffleChartDataSingle(scoreDistribution['high']),
          });
        }
        const scoreClusterDecomposedDistribution = parsedData['score_cluster_decomposed_distribution'];

        if (scoreClusterDecomposedDistribution) {
          setDecomposedDistribution({
            bad: scoreClusterDecomposedDistribution['bad'] || {
              a: 0,
              n: 0,
              p: '0%',
              raw_p: 0,
            },
            poor: scoreClusterDecomposedDistribution['poor'] || {
              a: 0,
              n: 0,
              p: '0%',
              raw_p: 0,
            },
            moderate: scoreClusterDecomposedDistribution['moderate'] || {
              a: 0,
              n: 0,
              p: '0%',
              raw_p: 0,
            },
            good: scoreClusterDecomposedDistribution['good'] || {
              a: 0,
              n: 0,
              p: '0%',
              raw_p: 0,
            },
            high: scoreClusterDecomposedDistribution['high'] || {
              a: 0,
              n: 0,
              p: '0%',
              raw_p: 0,
            },
          });
        } else {
          setDecomposedDistribution(undefined);
        }

        const _falseNegativeMetrics = parsedData['false_negative'];
        if (_falseNegativeMetrics) {
          setFalseNegativeMetrics(_falseNegativeMetrics);
        }

        const _falsePositiveMetrics = parsedData['false_positive_plotter'];
        if (_falsePositiveMetrics) {
          setFalsePositiveMetrics(_falsePositiveMetrics);
        }

        const relativePercentages = calculateMinMaxRelativePercentage(valuesArray);
        const alertsChartFeatureImportance: IAlertAnalytic[] = featureImportanceData.map((e, i) => {
          return {
            label: e?.name,
            hits: relativePercentages?.relativePercentage[i]?.relativePercentage || 0,
          };
        });
        const koPercentageData = parsedData['ko_percentage_per_value'];

        if (parsedFeatureProductMapping) {
          const featureProducts = Object.keys(parsedFeatureProductMapping)
            .map((e) => {
              return getActiveProduct(e);
            })
            .filter((value, index, self) => self.indexOf(value) === index);
          setProductLabels(featureProducts);
          setVerticalBarChartData(alertsChartFeatureImportance);
          setKoDataArray(koPercentageData);
          setConfusionMatrixData(parsedData['confusion_matrix']);
          Object.keys(parsedFeatureProductMapping).forEach((e) => {
            if (parsedFeatureProductMapping[e].includes(featureImportanceData[0]?.name)) {
              const actProduct = getActiveProduct(e);
              setActiveProduct(actProduct);
            }
          });
        }
        setFeatureName(featureImportanceData[0]?.name);
        setRiskChartKo(replaceKeys(parsedData['score_cluster_distribution_ko']));
        setRiskChartOk(replaceKeys(parsedData['score_cluster_distribution_ok']));
      }
    }
  }, [dataAnalysis]);

  const pushToCustomerPage = (resolution_id: string) => {
    if (resolution_id) window.open(`/customer/${resolution_id}`, '_blank');
  };

  React.useEffect(() => {
    if (dataAnalysis?.item?.value) {
      const parsedData = jsonParse<Record<string, any>>(dataAnalysis.item?.value);
      if (parsedData) {
        setParsedData(parsedData);
        const fraudVsRejection = parsedData['fraud_vs_rejection'];
        if (fraudVsRejection) {
          setFroudVsRejection(fraudVsRejection.data);
          const list: FroudVsRejection[] = fraudVsRejection.data;
          const _areaChartData: LineChartDataType[] = list.map((e) => ({
            x: e.fdr,
            y: e.arr,
            label: `Fraud detection rate: ${e.fdr}%
           Approval reduction rate: ${e.arr}%`,
          }));
          setLineChartData(_areaChartData);
        }
        if (getScatterCluster(activeTab) === 'full') {
          const fullScatterData: Record<string, any> = [
            ...(parsedData['scatter_strip']['poor'] || []),
            ...(parsedData['scatter_strip']['bad'] || []),
            ...(parsedData['scatter_strip']['moderate'] || []),
            ...(parsedData['scatter_strip']['good'] || []),
            ...(parsedData['scatter_strip']['high'] || []),
          ];

          setScatterData(fullScatterData);
        } else {
          setScatterData(parsedData['scatter_strip'][getScatterCluster(activeTab)]);
        }
      }
    }
  }, [dataAnalysis, activeTab]);

  return (
    <>
      <Grid item xs={12} md={12}>
        <ChartWrapper
          components={[
            {
              title: 'Sample distribution across score',
              infoData: scatterPlotTooltip,
              chart: {
                type: 'insightScatterChart',
                data: [
                  ...((parsedScatterData && scatterData && parsedScatterData['scatter_strip']['poor']) || []),
                  ...((parsedScatterData && scatterData && parsedScatterData['scatter_strip']['bad']) || []),
                  ...((parsedScatterData && scatterData && parsedScatterData['scatter_strip']['moderate']) || []),
                  ...((parsedScatterData && scatterData && parsedScatterData['scatter_strip']['good']) || []),
                  ...((parsedScatterData && scatterData && parsedScatterData['scatter_strip']['high']) || []),
                ],
                cluster: activeTab,

                history: pushToCustomerPage,
              },
              tabLabel: 'All',
            },
            {
              title: 'Sample distribution across score',
              chart: {
                type: 'insightScatterChart',
                data: [...((parsedScatterData && scatterData && parsedScatterData['scatter_strip']['poor']) || [])],
                cluster: activeTab,

                history: pushToCustomerPage,
              },
              tabLabel: 'Poor',
            },
            {
              title: 'Sample distribution across score',
              chart: {
                type: 'insightScatterChart',
                data: [...((parsedScatterData && scatterData && parsedScatterData['scatter_strip']['bad']) || [])],
                cluster: activeTab,

                history: pushToCustomerPage,
              },
              tabLabel: 'Bad',
            },
            {
              title: 'Sample distribution across score',
              chart: {
                type: 'insightScatterChart',
                data: [...((parsedScatterData && scatterData && parsedScatterData['scatter_strip']['moderate']) || [])],
                cluster: activeTab,

                history: pushToCustomerPage,
              },
              tabLabel: 'Moderate',
            },
            {
              title: 'Sample distribution across score',
              chart: {
                type: 'insightScatterChart',
                data: [...((parsedScatterData && scatterData && parsedScatterData['scatter_strip']['good']) || [])],
                cluster: activeTab,

                history: pushToCustomerPage,
              },
              tabLabel: 'Good',
            },
            {
              title: 'Sample distribution across score',
              chart: {
                type: 'insightScatterChart',
                data: [...((parsedScatterData && scatterData && parsedScatterData['scatter_strip']['high']) || [])],
                cluster: activeTab,

                history: pushToCustomerPage,
              },
              tabLabel: 'High',
            },
          ]}
          isLoading={isLoadingDataAnalysis}
          isError={isErrorDataAnalysis}
        />
      </Grid>

      <Grid item xs={12}>
        <Card style={{ minHeight: decomposedDistribution ? 400 : 0 }}>
          <CardHeader title="Score Cluster Distribution" style={{ paddingLeft: 17 }} />
          <RowOnDesktop style={{ display: 'flex' }}>
            {isLoadingDataAnalysis &&
              scoreClusterLabelsValues.map((label, i) => {
                const data: Record<'bad' | 'poor' | 'moderate' | 'good' | 'high' | 'empty', { x: number; y: number }[]> = {
                  bad: [],
                  poor: [],
                  moderate: [],
                  good: [],
                  high: [],
                  empty: [],
                };
                const percentage = '0%';
                const absValue = '0';
                return (
                  <ChartWrapper
                    key={i}
                    components={[
                      {
                        title: '',
                        tabLabel: '',
                        chart: { type: 'scoreClusterWaffleChart', data: data, label: label, percentage: percentage, absValue: absValue },
                      },
                    ]}
                    noWrapperStyle={true}
                    isLoading={isLoadingDataAnalysis}
                    customPadding={{ top: 0, bottom: 24, left: 0, right: 0 }}
                  />
                );
              })}
            {waffleChartClusterData &&
              decomposedDistribution &&
              scoreClusterLabelsValues.map((label, i) => {
                const data: Record<'bad' | 'poor' | 'moderate' | 'good' | 'high' | 'empty', { x: number; y: number }[]> = {
                  bad: label === 'bad' ? waffleChartClusterData['bad'] : [],
                  poor: label === 'poor' ? waffleChartClusterData['poor'] : [],
                  moderate: label === 'moderate' ? waffleChartClusterData['moderate'] : [],
                  good: label === 'good' ? waffleChartClusterData['good'] : [],
                  high: label === 'high' ? waffleChartClusterData['high'] : [],
                  empty: [],
                };
                const percentage = decomposedDistribution[label] ? decomposedDistribution[label]['p'] : '0%';
                const absValue = decomposedDistribution[label] ? decomposedDistribution[label]['a'] : '0';
                return (
                  <ChartWrapper
                    key={i}
                    components={[
                      {
                        title: '',
                        tabLabel: '',
                        chart: { type: 'scoreClusterWaffleChart', data: data, label: label, percentage: percentage, absValue: absValue },
                      },
                    ]}
                    noWrapperStyle={true}
                    customPadding={{ top: 0, bottom: 24, left: 0, right: 0 }}
                  />
                );
              })}
            {(waffleChartClusterData === undefined || decomposedDistribution === undefined) && !isLoadingDataAnalysis && !isErrorDataAnalysis && <EmptyState />}
            {isErrorDataAnalysis && <FailureScreen>Something went wrong. Please refresh the page and try again.</FailureScreen>}
          </RowOnDesktop>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <ConfusionMatrixTable isLoading={isLoadingDataAnalysis} isError={isErrorDataAnalysis} data={confusionMatrixData} />
      </Grid>

      <Grid item xs={12} md={6}>
        <ChartWrapper
          isError={!isLoadingDataAnalysis && (!lineChartData || !froudVsRejection)}
          isLoading={isLoadingDataAnalysis}
          components={[
            {
              title: 'Fraud detection rate Vs Approval reduction rate',
              chart: { type: 'fraudDetectionVsApprovalReductionChart', data: lineChartData || [], fvsr: froudVsRejection || [] },
              tabLabel: '',
              isEmpty: !isLoadingDataAnalysis && !lineChartData,
            },
          ]}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Card style={{ minHeight: 400 }}>
          <CardHeader
            title="OK score breakdown"
            action={
              <div
                style={{
                  float: 'right',
                  paddingRight: theme.spacing(1),
                }}
              >
                <CustomWidthTooltip title={okDonutTooltip}>
                  <InfoOutlinedIcon />
                </CustomWidthTooltip>
              </div>
            }
          />
          <>
            {riskChartOk ? (
              <ErrorBoundary>
                <React.Suspense fallback={<LoadingScreen variant="circle" />}>
                  <RiskChart segmentation={riskChartOk} dataType="score" />
                </React.Suspense>
              </ErrorBoundary>
            ) : (
              <RiskChartSkeleton />
            )}
          </>
        </Card>
      </Grid>

      {riskChartKo && (
        <Grid item xs={12} md={6}>
          <Card style={{ minHeight: 400 }}>
            <CardHeader
              title="KO score breakdown"
              action={
                <div
                  style={{
                    float: 'right',
                    paddingRight: theme.spacing(1),
                  }}
                >
                  <CustomWidthTooltip title={koDonutTooltip}>
                    <InfoOutlinedIcon />
                  </CustomWidthTooltip>
                </div>
              }
            />
            <ErrorBoundary>
              <React.Suspense fallback={<LoadingScreen variant="circle" />}>
                <RiskChart segmentation={riskChartKo} dataType="score" />
              </React.Suspense>
            </ErrorBoundary>
          </Card>
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="False negative" />
          {falseNegativeMetrics && falseNegativeMetrics.data.items.length > 0 ? (
            <CustomerMetricsTable items={falseNegativeMetrics.data.items} />
          ) : (
            <PaddingBox component="div">
              <EmptyState />
            </PaddingBox>
          )}
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="False positive" />
          {falsePositiveMetrics && falsePositiveMetrics.data.items.length > 0 ? (
            <CustomerMetricsTable items={falsePositiveMetrics.data.items} />
          ) : (
            <PaddingBox component="div">
              <EmptyState />
            </PaddingBox>
          )}
        </Card>
      </Grid>
    </>
  );
};

export default AccuracyAnalysis;

const PaddingBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));
